import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

/**
 * Verstaani avustaja conversation state + sending, shared by the floating HelpWidget (corner
 * panel and large dialog) and the embedded chat on the support page (/support).
 */
export default function useHelpChat() {
  const { t } = useTranslation();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const newChat = () => setMessages([]);

  const sendMessage = async (text) => {
    const userMessage = { role: 'user', content: text };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setLoading(true);

    try {
      // Build the messages payload (just role + content for the API). Error bubbles are
      // chat decoration, not conversation — resending them would feed the model fake
      // assistant turns saying the chat failed.
      const apiMessages = updatedMessages
        .filter((msg) => !msg.isError)
        .map(({ role, content }) => ({ role, content }));

      const response = await axios.post('/api/chatbot/chat/', {
        messages: apiMessages,
        current_page: location.pathname,
      });

      // support_request: the model's draft of a support request when the guides did not
      // answer the question; the panel then offers one-click escalation under the answer
      const { answer, suggestions, support_request: supportRequest } = response.data;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: answer, suggestions, supportRequest: supportRequest || '' },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: t('helpChatbot.error'),
          suggestions: [],
          isError: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return { messages, loading, sendMessage, newChat };
}
